import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from 'src/store/auth';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { fillSelection } from 'src/devtools/fillers/selection';

import { W2Shell } from '../components/W2Shell';
import { W2Done, type W2DoneReason } from '../components/W2Done';
import {
  SelectionApiError,
  getSelectionContext,
  normalizeApiCode,
  saveElections,
  sign,
} from './api/selection';
import { categoryRank } from './constants/categories';
import { usePlanDetails } from './api/planDetails';
import { useSelectionStore } from './store/selection';
import { W2_SELECT_TID } from './constants/testids';
import { stepModel, type StepKey } from './constants/steps';
import { todayISO } from './format';
import { SELECTION_LOGIN_REDIRECT } from './routes';
import { SelectionWelcome } from './components/SelectionWelcome';
import { PlanScreen } from './components/PlanScreen';
import { QuoteReview } from './components/QuoteReview';
import { AgreementSignature } from './components/AgreementSignature';
import { SelectionDone } from './components/SelectionDone';
import { SelectionScaffold } from './components/SelectionScaffold';
type Phase = 'welcome' | 'plans' | 'quote' | 'agreement' | 'done';

type ResolveState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'locked' }
  | {
      kind: 'ineligible';
      reason: Extract<
        W2DoneReason,
        | 'waive'
        | 'questionnaire-pending'
        | 'selection-closed'
        | 'selection-unavailable'
        | 'not-enrolled'
      >;
    }
  | { kind: 'multiEmployer' }
  | { kind: 'error' };

/**
 * Orchestrates the W2 employee plan self-selection flow (WP6). Unlike the token
 * PHQ flow (`W2FlowLayout` at `/e/<token>`), this is a RETURNING,
 * normally-authenticated surface at `/select-plans` — NO token. It:
 *
 *   1. guards on the JWT (redirect to `/login?next=/select-plans` if absent);
 *   2. resolves `GET /employee/selection-context` (the single selection-eligible
 *      Self employer — section Onboarding OR Active per the frozen contract D1 —
 *      + offerings), routing the §4 gate rejections — and a PHQ-waived employee
 *      (`phqStatus === 'Waived'`, excluded per selinv contract D2) — to terminal
 *      states;
 *   3. drives phases welcome → plans (one screen per offered category) → quote →
 *      agreement → done, requiring an EXPLICIT decision on every offered line
 *      before the quote unlocks (scope §Waive model).
 */
export const SelectionFlowPage: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const hydrate = useSelectionStore((s) => s.hydrate);
  const reset = useSelectionStore((s) => s.reset);
  const setQuote = useSelectionStore((s) => s.setQuote);
  const toElectionInputs = useSelectionStore((s) => s.toElectionInputs);
  const context = useSelectionStore((s) => s.context);
  const quote = useSelectionStore((s) => s.quote);
  // WP6: join offering.planId → full InsurancePlan for card attributes. Fetched
  // once, cached, and gracefully absent ({}) on any failure — never blocks.
  const planDetails = usePlanDetails();

  const [resolveState, setResolveState] = useState<ResolveState>({
    kind: 'loading',
  });
  const [phase, setPhase] = useState<Phase>('welcome');
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Resolve the selection context once the JWT is present.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setResolveState({ kind: 'loading' });
    (async () => {
      try {
        const ctx = await getSelectionContext();
        if (cancelled) return;
        // A signature is the terminal authority even if an inconsistent legacy
        // row carries another PHQ status: already selected + signed always wins.
        if (ctx.locked) {
          hydrate(ctx);
          setResolveState({ kind: 'locked' });
          return;
        }
        // PHQ-waived employees declined coverage outright (W2WaiverModal) and are
        // EXCLUDED from self-selection (selinv contract D2). Use the honest waiver
        // terminal, never the completed-questionnaire lockout copy.
        if (ctx.phqStatus === 'Waived') {
          setResolveState({
            kind: 'ineligible',
            reason: 'waive',
          });
          return;
        }
        // Only a completed PHQ may enter plan selection. Token-flow logins are
        // routed directly to `/e/:token/phq` by W2FlowLayout; a standalone login
        // has no employer token in the selection-context contract, so fail closed
        // with truthful resume instructions instead of exposing the plan UI.
        if (ctx.phqStatus === 'NotStarted' || ctx.phqStatus === 'InProgress') {
          setResolveState({
            kind: 'ineligible',
            reason: 'questionnaire-pending',
          });
          return;
        }
        hydrate(ctx);
        setResolveState({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setResolveState(classifyResolveError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, hydrate, reloadKey]);

  // Clear the working store when leaving the flow.
  useEffect(() => () => reset(), [reset]);

  /** Offered categories, de-duped and ordered (Medical → … → Hospital Indemnity). */
  const categories = useMemo(() => {
    const present = Array.from(
      new Set((context?.offerings ?? []).map((o) => o.category))
    );
    return present.sort((a, b) => categoryRank(a) - categoryRank(b));
  }, [context]);

  if (!isAuthenticated) {
    return <Navigate to={SELECTION_LOGIN_REDIRECT} replace />;
  }

  const retry = () => setReloadKey((k) => k + 1);

  // Jump-back from a completed step bar cell → the phase/category it represents
  // (the bar only fires this for completed steps; see W2SelectionStepBar).
  const navToStep = (key?: StepKey) => {
    switch (key) {
      case 'welcome':
        setPhase('welcome');
        break;
      case 'health': {
        const i = categories.indexOf('Medical');
        setCategoryIndex(i < 0 ? 0 : i);
        setPhase('plans');
        break;
      }
      case 'extra': {
        const i = categories.findIndex((c) => c !== 'Medical');
        setCategoryIndex(i < 0 ? 0 : i);
        setPhase('plans');
        break;
      }
      case 'review':
        setPhase('quote');
        break;
      case 'agreement':
        setSignError(null);
        setPhase('agreement');
        break;
      default:
        break;
    }
  };

  if (resolveState.kind === 'loading') {
    return (
      <W2Shell title="One moment…" showLogout>
        <p
          data-testid={W2_SELECT_TID.loading}
          className="text-[15px] text-[#5a5a70]"
        >
          Loading your plan selection…
        </p>
      </W2Shell>
    );
  }

  if (resolveState.kind === 'error') {
    return (
      <W2Shell
        showLogout
        title="We couldn’t load your plan selection"
        description="Something went wrong on our side. Please try again in a moment."
      >
        <div data-testid={W2_SELECT_TID.loadError}>
          <button
            type="button"
            data-testid={W2_SELECT_TID.retry}
            onClick={retry}
            className="rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:opacity-90"
          >
            Try again
          </button>
        </div>
      </W2Shell>
    );
  }

  if (resolveState.kind === 'multiEmployer') {
    return (
      <W2Shell title="More than one employer" showLogout>
        <p
          data-testid={W2_SELECT_TID.multiEmployer}
          className="text-[15px] leading-6 text-[#5a5a70]"
        >
          Your account is linked to more than one employer offering plan
          selection. Please contact your benefits team so they can point you to
          the right one.
        </p>
      </W2Shell>
    );
  }

  if (resolveState.kind === 'ineligible') {
    // Preserve the shared W2 terminal/testids, but never collapse distinct
    // eligibility causes onto the PHQ "Questionnaire Already Completed" copy.
    return <W2Done reason={resolveState.reason} />;
  }

  if (resolveState.kind === 'locked' || phase === 'done') {
    // Terminal, immutable state (just-signed OR arrived-already-locked): the
    // client edit-lock must be REAL, not illusory. The done step bar marks every
    // cell completed, so we deliberately pass NO `onStepClick` — completed cells
    // then carry no button semantics (see W2SelectionStepBar) and there is no way
    // to navigate back into an editable/signable phase (finding #6 / fe-fb #1).
    const model = stepModel(categories, 'done');
    return (
      <SelectionScaffold
        priceOverride={quote?.total ?? null}
        steps={model.steps}
      >
        <SelectionDone alreadyLocked={resolveState.kind === 'locked'} />
      </SelectionScaffold>
    );
  }

  if (phase === 'welcome') {
    const model = stepModel(categories, 'welcome');
    return (
      <SelectionScaffold
        steps={model.steps}
        onStepClick={(i) => navToStep(model.steps[i]?.key)}
      >
        <SelectionWelcome
          step={model.activeIndex + 1}
          total={model.steps.length}
          onStart={() => {
            setCategoryIndex(0);
            setPhase('plans');
          }}
        />
      </SelectionScaffold>
    );
  }

  if (phase === 'plans') {
    // No offered categories at all — nothing to elect; go straight to the quote.
    if (categories.length === 0) {
      const model = stepModel(categories, 'plans');
      return (
        <SelectionScaffold
          priceOverride={quote?.total ?? null}
          steps={model.steps}
          onStepClick={(i) => navToStep(model.steps[i]?.key)}
        >
          <QuoteReview
            step={model.activeIndex + 1}
            total={model.steps.length}
            onBack={() => setPhase('welcome')}
            onContinue={() => setPhase('agreement')}
          />
        </SelectionScaffold>
      );
    }
    const idx = Math.min(categoryIndex, categories.length - 1);
    const category = categories[idx];
    const isFirst = idx === 0;
    const isLast = idx === categories.length - 1;

    const goNext = async () => {
      setSaveError(null);
      if (!isLast) {
        setCategoryIndex(idx + 1);
        return;
      }
      // Last category → persist the full per-line set and fetch the server quote.
      setSaving(true);
      try {
        const result = await saveElections(toElectionInputs());
        setQuote(result.quote);
        setPhase('quote');
      } catch (err) {
        if (
          err instanceof SelectionApiError &&
          normalizeApiCode(err.code) === 'error.electionLocked'
        ) {
          setResolveState({ kind: 'locked' });
          return;
        }
        setSaveError(
          err instanceof SelectionApiError
            ? err.message
            : 'We couldn’t save your selections. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    };

    const model = stepModel(categories, 'plans', category);
    return (
      <SelectionScaffold
        steps={model.steps}
        onStepClick={(i) => navToStep(model.steps[i]?.key)}
      >
        <PlanScreen
          category={category}
          step={model.activeIndex + 1}
          total={model.steps.length}
          isFirst={isFirst}
          isLast={isLast}
          saving={saving}
          saveError={saveError}
          planDetails={planDetails}
          onBack={() => {
            if (isFirst) {
              setPhase('welcome');
            } else {
              setCategoryIndex(idx - 1);
            }
          }}
          onNext={goNext}
        />
        {/* DEV-only: elect every offered line at once (real planIds/tiers). */}
        {DEVFILL_ENABLED && (
          <DevFillButton
            testid={DEVFILL_TID.selection}
            label="Elect all plans"
            onFill={() => fillSelection()}
          />
        )}
      </SelectionScaffold>
    );
  }

  if (phase === 'quote') {
    const model = stepModel(categories, 'quote');
    return (
      <SelectionScaffold
        priceOverride={quote?.total ?? null}
        steps={model.steps}
        onStepClick={(i) => navToStep(model.steps[i]?.key)}
      >
        <QuoteReview
          step={model.activeIndex + 1}
          total={model.steps.length}
          onBack={() => {
            setCategoryIndex(Math.max(categories.length - 1, 0));
            setPhase('plans');
          }}
          onContinue={() => {
            setSignError(null);
            setPhase('agreement');
          }}
          onChangeCategory={(category) => {
            const i = categories.indexOf(category);
            setCategoryIndex(i < 0 ? 0 : i);
            setPhase('plans');
          }}
        />
      </SelectionScaffold>
    );
  }

  // phase === 'agreement'
  const onFinish = async (signerName: string) => {
    setSigning(true);
    setSignError(null);
    try {
      await sign({
        signerName,
        typedSignature: signerName,
        date: todayISO(),
        authorized: true,
      });
      setPhase('done');
    } catch (err) {
      if (
        err instanceof SelectionApiError &&
        normalizeApiCode(err.code) === 'error.alreadySigned'
      ) {
        // Signed elsewhere/already — land on the truthful terminal state.
        setResolveState({ kind: 'locked' });
        return;
      }
      setSignError(
        err instanceof SelectionApiError
          ? err.message
          : 'We couldn’t record your signature. Please try again.'
      );
    } finally {
      setSigning(false);
    }
  };

  const agreementModel = stepModel(categories, 'agreement');
  return (
    <SelectionScaffold
      priceOverride={quote?.total ?? null}
      steps={agreementModel.steps}
      onStepClick={(i) => navToStep(agreementModel.steps[i]?.key)}
    >
      <AgreementSignature
        step={agreementModel.activeIndex + 1}
        total={agreementModel.steps.length}
        signing={signing}
        signError={signError}
        onBack={() => setPhase('quote')}
        onFinish={onFinish}
      />
    </SelectionScaffold>
  );
};

/** Map a §4 context-resolve error to the terminal state it should render. */
function classifyResolveError(err: unknown): ResolveState {
  if (!(err instanceof SelectionApiError)) return { kind: 'error' };
  switch (normalizeApiCode(err.code)) {
    case 'error.multipleEmployers':
      return { kind: 'multiEmployer' };
    case 'error.notSelfSelfMode':
    case 'error.notSelfMode':
    case 'error.notSmallGroup':
      return {
        kind: 'ineligible',
        reason: 'selection-unavailable',
      };
    case 'error.notActive':
      return {
        kind: 'ineligible',
        reason: 'selection-closed',
      };
    case 'error.employeeNotEnrolled':
      return {
        kind: 'ineligible',
        reason: 'not-enrolled',
      };
    default:
      // 409 multi-employer may arrive without a code but with candidates.
      if (err.status === 409 && err.candidates)
        return { kind: 'multiEmployer' };
      return { kind: 'error' };
  }
}

export default SelectionFlowPage;
